import { ApolloError, ApolloLink } from '@apollo/client';

const STORAGE_KEY = 'blackroom:invitation';
export const INVITATION_TOKEN_HEADER = 'X-Invitation-Token';

// Reasons the API refuses to bind a first login to an account. Sent in the
// 401 body (`code`), i.e. GraphQL `extensions.originalError.code`.
export const AUTH_IDENTITY_ERROR_CODES = [
  'EMAIL_NOT_VERIFIED',
  'INVITATION_INVALID',
  'INVITATION_EMAIL_MISMATCH',
  'ACCOUNT_LINK_REQUIRED',
  'ACCOUNT_UNAVAILABLE',
  // Sessions opened before a password reset were revoked at the binding.
  'SESSION_RENEWAL_REQUIRED',
] as const;

export type AuthIdentityErrorCode = (typeof AUTH_IDENTITY_ERROR_CODES)[number];

type Invitation = { token?: string; email?: string };

const SESSION_RENEWAL_KEY = 'blackroom:session-renewal';
const SESSION_RENEWAL_COOLDOWN_MS = 2 * 60 * 1000;

// The invitation outlives the Auth0 redirect (which drops the query string)
// for this tab only.
function read(): Invitation {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Invitation) : {};
    return {
      token: typeof parsed.token === 'string' ? parsed.token : undefined,
      email: typeof parsed.email === 'string' ? parsed.email : undefined,
    };
  } catch {
    return {};
  }
}

function write(invitation: Invitation): void {
  try {
    if (invitation.token || invitation.email) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(invitation));
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage unavailable: the invitee falls back to email verification.
  }
}

/**
 * Keeps the `invite` token and `email` of an invitation link, and removes the
 * token from the address bar so it does not linger in history or referrers.
 */
export function captureInvitationFromUrl(): void {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('invite');
  const email = url.searchParams.get('email');
  if (!token) return;
  write({ token, email: email || undefined });
  url.searchParams.delete('invite');
  window.history.replaceState(window.history.state, '', url.href);
}

export function getInvitationToken(): string | null {
  return read().token ?? null;
}

export function getInvitedEmail(): string | null {
  return read().email ?? null;
}

/** Drops the token only: the invited email still prefills the login form. */
export function discardInvitationToken(): void {
  write({ email: read().email });
}

export function clearInvitation(): void {
  write({});
}

export function getInvitationRequestHeaders(): Record<string, string> {
  const token = getInvitationToken();
  return token ? { [INVITATION_TOKEN_HEADER]: token } : {};
}

/** Prefill for the Auth0 form: the link's email, else the stored invitation. */
export function getLoginHint(search: string): string | undefined {
  return (
    new URLSearchParams(search).get('email') || getInvitedEmail() || undefined
  );
}

function isAuthIdentityErrorCode(code: unknown): code is AuthIdentityErrorCode {
  return AUTH_IDENTITY_ERROR_CODES.includes(code as AuthIdentityErrorCode);
}

type ErrorWithExtensions = { extensions?: Record<string, unknown> | null };

function identityCodeOf(
  error: ErrorWithExtensions,
): AuthIdentityErrorCode | null {
  const original = error.extensions?.originalError as
    | { code?: unknown }
    | undefined;
  return isAuthIdentityErrorCode(original?.code) ? original.code : null;
}

export function getAuthIdentityErrorCode(
  error: ApolloError | undefined | null,
): AuthIdentityErrorCode | null {
  if (!error) return null;
  const networkResult = (
    error.networkError as { result?: { errors?: ErrorWithExtensions[] } } | null
  )?.result;
  const errors: ErrorWithExtensions[] = [
    ...(error.graphQLErrors ?? []),
    ...(networkResult?.errors ?? []),
  ];
  for (const graphQLError of errors) {
    const code = identityCodeOf(graphQLError);
    if (code) return code;
  }
  return null;
}

function clearSessionRenewal(): void {
  try {
    window.sessionStorage.removeItem(SESSION_RENEWAL_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * True at most once per cooldown, so an automatic sign-in that does not fix
 * SESSION_RENEWAL_REQUIRED cannot loop: the notice is shown instead.
 */
export function claimSessionRenewal(now = Date.now()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(SESSION_RENEWAL_KEY));
    if (last && now - last < SESSION_RENEWAL_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(SESSION_RENEWAL_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/**
 * Sends the invitation token until the account is bound (first successful
 * `GetMe`), stops sending a token the API rejected as invalid, and signs in
 * again when the API revoked the sessions predating the binding.
 */
export function createInvitationLink({
  onSessionRenewalRequired,
}: {
  onSessionRenewalRequired: () => void;
}): ApolloLink {
  return new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: { ...headers, ...getInvitationRequestHeaders() },
    }));
    return forward(operation).map((result) => {
      if (operation.operationName === 'GetMe' && result.data?.me) {
        clearInvitation();
        clearSessionRenewal();
      } else if (
        result.errors?.some(
          (error) => identityCodeOf(error) === 'INVITATION_INVALID',
        )
      ) {
        discardInvitationToken();
      }
      if (
        result.errors?.some(
          (error) => identityCodeOf(error) === 'SESSION_RENEWAL_REQUIRED',
        ) &&
        claimSessionRenewal()
      ) {
        onSessionRenewalRequired();
      }
      return result;
    });
  });
}

/** Asks the API to email a fresh invitation link to the invited address. */
export async function requestInvitationResend(accessToken: string) {
  const response = await fetch(
    `${import.meta.env.VITE_API_CORE_REST_URL}/invitations/resend`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${accessToken}` },
    },
  );
  if (!response.ok) {
    throw new Error(`Invitation resend failed: ${response.status}`);
  }
}
